import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserInfo } from '../utils/tokenManager';
import { getPriceInfoForReservation, submitReservation, getOrderSaleStatus, getAgencyList } from '../services/apiService';

const GeneralReservationModal = ({ open, onClose, priceNo, datePriceNo, date, productName }) => {
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const [priceInfo, setPriceInfo] = useState(null);
  const [formData, setFormData] = useState({
    adultCount: 1,
    childCount: 0,
    childAges: [],
    ordererName: '',
    ordererPhone: '',
    ordererEmail: '',
    selectedOptions: [],
    agencyCode: ''
  });
  const [errors, setErrors] = useState({});
  const [mounted, setMounted] = useState(false);
  const [animatingOut, setAnimatingOut] = useState(false);
  const debounceRef = useRef(null);
  const [agencyList, setAgencyList] = useState([]);
  const [agencyLoading, setAgencyLoading] = useState(false);
  const [agencyDropdownOpen, setAgencyDropdownOpen] = useState(false);
  const [agencySearchTerm, setAgencySearchTerm] = useState('');
  const agencyDropdownRef = useRef(null);

  useEffect(() => {
    if (open) {
      setAnimatingOut(false);
      const id = requestAnimationFrame(() => setMounted(true));
      loadAgencyList();
      return () => cancelAnimationFrame(id);
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (agencyDropdownRef.current && !agencyDropdownRef.current.contains(e.target)) {
        setAgencyDropdownOpen(false);
      }
    };
    if (agencyDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [agencyDropdownOpen]);

  useEffect(() => {
    if (open && (priceNo || datePriceNo)) {
      setInitialLoaded(false);
      setPriceInfo(null);
      fetchPriceInfo();
    }
  }, [open, priceNo, datePriceNo]);

  useEffect(() => {}, [formData.adultCount, formData.childCount, formData.childAges]);

  const loadAgencyList = async () => {
    try {
      setAgencyLoading(true);
      const { companyNo } = getUserInfo() || {};
      if (!companyNo) return;
      const list = await getAgencyList({ companyNo });
      setAgencyList(list);
    } catch (e) {
      console.error('Failed to load agency list:', e);
    } finally {
      setAgencyLoading(false);
    }
  };

  const filteredAgencyList = agencyList.filter((agency) => {
    const name = String(agency.코드명 || '').toLowerCase();
    const search = agencySearchTerm.toLowerCase();
    return name.includes(search);
  });

  const handleAgencySelect = (agency) => {
    setFormData((prev) => {
      const updated = { ...prev, agencyCode: agency.코드 };
      const sortedChildAges = [...updated.childAges].sort((a, b) => a - b);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchPriceInfoWithParams({
          adultCount: updated.adultCount,
          childAges: sortedChildAges,
          agencyCode: updated.agencyCode,
        });
      }, 250);
      return updated;
    });
    setAgencySearchTerm(agency.코드명);
    setAgencyDropdownOpen(false);
  };

  const getSelectedAgencyName = () => {
    const selected = agencyList.find((a) => a.코드 === formData.agencyCode);
    return selected ? selected.코드명 : '';
  };

  const fetchPriceInfo = async () => {
    const { adultCount, childAges } = formData;
    await fetchPriceInfoWithParams({
      adultCount,
      childAges: [...childAges].sort((a, b) => a - b),
      agencyCode: formData.agencyCode,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleAdultCountChange = (e) => {
    const value = parseInt(e.target.value, 10) || 1;
    setFormData(prev => {
      const updated = { ...prev, adultCount: value };
      const sortedChildAges = [...updated.childAges].sort((a, b) => a - b);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchPriceInfoWithParams({ adultCount: updated.adultCount, childAges: sortedChildAges, agencyCode: updated.agencyCode });
      }, 250);
      return updated;
    });
  };

  const handleChildCountChange = (e) => {
    const value = parseInt(e.target.value, 10) || 0;
    const oldCount = formData.childCount;
    let newAges = [...formData.childAges];
    if (value > oldCount) {
      for (let i = oldCount; i < value; i++) newAges.push(0);
    } else if (value < oldCount) {
      newAges = newAges.slice(0, value);
    }
    const sortedAges = [...newAges].sort((a, b) => a - b);
    setFormData(prev => {
      const updated = { ...prev, childCount: value, childAges: newAges };
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchPriceInfoWithParams({ adultCount: updated.adultCount, childAges: sortedAges, agencyCode: updated.agencyCode });
      }, 250);
      return updated;
    });
  };

  const fetchPriceInfoWithParams = async (params) => {
    try {
      if (!initialLoaded) setLoading(true); else setRefreshing(true);
      const { companyNo: loginCompanyNo } = getUserInfo() || {};
      const effectiveCompanyNo = params?.agencyCode
        ? String(params.agencyCode)
        : (loginCompanyNo != null ? String(loginCompanyNo) : '1');
      const sortedChildAges = [...params.childAges].sort((a, b) => a - b);
      const apiParams = {
        companyNo: effectiveCompanyNo,
        priceNo: datePriceNo || priceNo,
        nights: 1,
        adultCount: params.adultCount,
        ageArray: sortedChildAges.join(','),
        roomCount: 1,
        productTypeName: '투어',
      };
      const response = await getPriceInfoForReservation(apiParams);
      if (response && response.table && response.table.length > 0) {
        const rawData = response.table[0];
        if (rawData && rawData.정보) {
          try {
            const parsedInfo = JSON.parse(rawData.정보);
            if (parsedInfo && parsedInfo.객실상품 && parsedInfo.객실상품.length > 0) {
              const roomInfo = parsedInfo.객실상품[0];
              setPriceInfo(roomInfo);
            } else {
              setPriceInfo({});
            }
          } catch (parseError) {
            setPriceInfo({});
          }
        } else {
          setPriceInfo(rawData || {});
        }
      } else {
        setPriceInfo({});
      }
      if (!initialLoaded) setInitialLoaded(true);
    } catch (error) {
      console.error('Failed to fetch price info:', error);
      setPriceInfo({});
      if (!initialLoaded) setInitialLoaded(true);
    } finally {
      if (!initialLoaded) setLoading(false); else setRefreshing(false);
    }
  };

  const handleChildAgeChange = (index, age) => {
    const newAges = [...formData.childAges];
    newAges[index] = parseInt(age, 10) || 0;
    const sortedAges = [...newAges].sort((a, b) => a - b);
    setFormData(prev => {
      const updated = { ...prev, childAges: newAges };
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchPriceInfoWithParams({ adultCount: updated.adultCount, childAges: sortedAges, agencyCode: updated.agencyCode });
      }, 250);
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.ordererName.trim()) newErrors.ordererName = '주문자 이름을 입력하세요.';
    if (!formData.ordererPhone.trim()) newErrors.ordererPhone = '휴대폰 번호를 입력하세요.';
    else if (!/^\d{3}-?\d{3,4}-?\d{4}$/.test(formData.ordererPhone.replace(/-/g, ''))) newErrors.ordererPhone = '유효한 휴대폰 번호를 입력하세요.';
    if (!formData.ordererEmail.trim()) newErrors.ordererEmail = '이메일을 입력하세요.';
    else if (!/\S+@\S+\.\S+/.test(formData.ordererEmail)) newErrors.ordererEmail = '유효한 이메일 주소를 입력하세요.';
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    try {
      setLoading(true);
      const hasValidPriceInfo = priceInfo && typeof priceInfo === 'object' && Object.keys(priceInfo).length > 0;
      if (!hasValidPriceInfo) {
        alert('재고가 없거나 마감되어 예약이 불가능합니다.');
        return;
      }
      const { companyNo } = getUserInfo() || { companyNo: '1' };
      const reservationData = {
        priceNo: priceInfo.가격번호 || datePriceNo || priceNo,
        priceOptionNo: priceInfo.가격옵션번호 || 0,
        startDate: date,
        ordererName: formData.ordererName,
        ordererPhone: formData.ordererPhone,
        ordererEmail: formData.ordererEmail,
        adultCount: formData.adultCount,
        childCount: formData.childCount,
        childAges: formData.childAges,
        nights: 1,
        roomCount: 1,
        selectedOptions: [],
        totalAmount: priceInfo.결제하실금액 || 0,
        companyNo,
        productTypeName: '투어',
        agencyCode: formData.agencyCode || null,
      };
      const response = await submitReservation(reservationData);
      if (response && response.table && response.table.length > 0) {
        alert('예약이 완료되었습니다.');
        const firstRow = response.table[0] || {};
        const orderNo = firstRow.주문번호 ?? firstRow.orderNo ?? firstRow.OrderNo ?? null;
        let saleNo = firstRow.판매번호 ?? firstRow.주문판매번호 ?? firstRow.saleNo ?? null;
        try {
          if (!saleNo && orderNo) {
            const statusRow = await getOrderSaleStatus({ orderNo });
            saleNo = statusRow?.판매번호 ?? statusRow?.주문판매번호 ?? null;
          }
        } catch (e) {}
        if (orderNo) navigate(`/reservation-detail/${String(orderNo)}` , { state: { saleNo, productName } });
        else navigate('/reservations');
        requestClose();
      } else {
        alert('예약 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      console.error('Reservation error:', error);
      alert(`예약 중 오류가 발생했습니다: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setLoading(false);
    }
  };

  const requestClose = () => {
    setAnimatingOut(true);
    setMounted(false);
    window.setTimeout(() => { onClose && onClose(); }, 220);
  };

  if (!open) return null;

  const adultAgeInfo = priceInfo?.성인나이 || '(만 12세 이상)';
  const childAgeInfo = priceInfo?.아동나이 || '(만 12세 미만)';
  const parsedMaxChildAge = Number(priceInfo?.아동나이2);
  const maxChildAge = Number.isFinite(parsedMaxChildAge) && parsedMaxChildAge >= 0 ? parsedMaxChildAge : 12;
  const roomPrice = priceInfo?.객실요금 || 0;
  const optionPrice = priceInfo?.옵션요금 || 0;
  const discountPrice = priceInfo?.할인요금 || 0;
  const totalPrice = priceInfo?.결제하실금액 || 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className={`bg-white w-full max-w-4xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl transform transition-all ${mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <div>
            <h3 className="text-[17px] font-bold text-gray-900">예약하기 (단품)</h3>
            <p className="mt-1 text-xs text-gray-600">
              {productName ? `${productName} · ` : ''}예약일: {date}
            </p>
          </div>
          <button type="button" className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100" onClick={requestClose}>
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 min-h-0 px-6 py-4 overflow-y-auto">
          {!initialLoaded ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-2">상품 정보</h4>
                <div className="bg-gray-50 p-3 rounded-xl">
                  <p className="font-medium text-gray-700">{productName || '상품명'}</p>
                  <p className="text-sm text-gray-600">예약일: {date}</p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-2">예약 대리점</h4>
                <div className="relative" ref={agencyDropdownRef}>
                  <div
                    className="w-full h-9 px-2.5 border border-gray-300 rounded-lg bg-white text-sm cursor-pointer flex items-center justify-between"
                    onClick={() => setAgencyDropdownOpen(!agencyDropdownOpen)}
                  >
                    <span className={formData.agencyCode ? 'text-gray-800' : 'text-gray-400'}>
                      {formData.agencyCode ? getSelectedAgencyName() : '대리점 미 선택시 직판으로 예약됩니다'}
                    </span>
                    <svg className={`w-4 h-4 text-gray-500 transition-transform ${agencyDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {agencyDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                      <div className="p-2 border-b border-gray-200">
                        <input
                          type="text"
                          className="w-full h-9 px-2.5 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                          placeholder="대리점 검색..."
                          value={agencySearchTerm}
                          onChange={(e) => setAgencySearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {agencyLoading ? (
                          <div className="px-3 py-4 text-center text-gray-500 text-sm">불러오는 중...</div>
                        ) : filteredAgencyList.length === 0 ? (
                          <div className="px-3 py-4 text-center text-gray-500 text-sm">
                            {agencySearchTerm ? '검색 결과가 없습니다' : '대리점이 없습니다'}
                          </div>
                        ) : (
                          filteredAgencyList.map((agency) => (
                            <div
                              key={agency.코드}
                              className={`px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm ${
                                formData.agencyCode === agency.코드 ? 'bg-blue-100 text-blue-800' : 'text-gray-700'
                              }`}
                              onClick={() => handleAgencySelect(agency)}
                            >
                              {agency.코드명}
                            </div>
                          ))
                        )}
                      </div>
                      {formData.agencyCode && (
                        <div className="border-t border-gray-200">
                          <div
                            className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-500 text-center"
                            onClick={() => {
                              setFormData((prev) => {
                                const updated = { ...prev, agencyCode: '' };
                                const sortedChildAges = [...updated.childAges].sort((a, b) => a - b);
                                if (debounceRef.current) clearTimeout(debounceRef.current);
                                debounceRef.current = setTimeout(() => {
                                  fetchPriceInfoWithParams({
                                    adultCount: updated.adultCount,
                                    childAges: sortedChildAges,
                                    agencyCode: updated.agencyCode,
                                  });
                                }, 250);
                                return updated;
                              });
                              setAgencySearchTerm('');
                              setAgencyDropdownOpen(false);
                            }}
                          >
                            선택 해제
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-2">인원 선택</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-[13px] font-semibold text-gray-600 mb-1">성인 {adultAgeInfo}</label>
                    <input type="number" inputMode="numeric" pattern="[0-9]*" name="adultCount" min={1} max={10} step={1} value={formData.adultCount} onChange={handleAdultCountChange} className="w-full h-9 px-2.5 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-gray-600 mb-1">아동 {childAgeInfo}</label>
                    <input type="number" inputMode="numeric" pattern="[0-9]*" name="childCount" min={0} max={5} step={1} value={formData.childCount} onChange={handleChildCountChange} className="w-full h-9 px-2.5 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                  </div>
                  {formData.childCount > 0 && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                      <p className="text-[13px] font-semibold text-gray-600 mb-2">아동 나이 선택</p>
                      <div className="space-y-2">
                        {Array.from({ length: formData.childCount }).map((_, index) => (
                          <div key={`child-age-${index}`} className="flex items-center">
                            <span className="text-sm text-gray-600 w-16">아동 {index + 1}</span>
                            <select value={formData.childAges[index] || 0} onChange={(e) => handleChildAgeChange(index, e.target.value)} className="ml-2 h-9 px-2.5 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500">
                              {Array.from({ length: maxChildAge + 1 }).map((_, age) => (
                                <option key={`age-${age}`} value={age}>{age}세</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-2">주문자 정보</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-[13px] font-semibold text-gray-600 mb-1">이름</label>
                    <input type="text" name="ordererName" value={formData.ordererName} onChange={handleChange} className={`w-full h-9 px-2.5 border ${errors.ordererName ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500`} placeholder="이름을 입력하세요" />
                    {errors.ordererName && (<p className="mt-1 text-sm text-red-600">{errors.ordererName}</p>)}
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-gray-600 mb-1">휴대폰</label>
                    <input type="tel" name="ordererPhone" value={formData.ordererPhone} onChange={handleChange} className={`w-full h-9 px-2.5 border ${errors.ordererPhone ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500`} placeholder="010-0000-0000" />
                    {errors.ordererPhone && (<p className="mt-1 text-sm text-red-600">{errors.ordererPhone}</p>)}
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-gray-600 mb-1">이메일</label>
                    <input type="email" name="ordererEmail" value={formData.ordererEmail} onChange={handleChange} className={`w-full h-9 px-2.5 border ${errors.ordererEmail ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500`} placeholder="example@email.com" />
                    {errors.ordererEmail && (<p className="mt-1 text-sm text-red-600">{errors.ordererEmail}</p>)}
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-800">요금 정보</h4>
                  {refreshing && (
                    <span className="inline-flex items-center text-xs text-gray-500">
                      <svg className="animate-spin h-4 w-4 mr-1" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      갱신중
                    </span>
                  )}
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="space-y-2">
                    <div className="flex justify-between"><span className="text-gray-600">상품 요금</span><span className="font-medium">{roomPrice.toLocaleString()}원</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">옵션 요금</span><span className="font-medium">{optionPrice.toLocaleString()}원</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">할인 요금</span><span className="font-medium text-red-600">-{discountPrice.toLocaleString()}원</span></div>
                    <div className="border-top border-gray-300 my-2 pt-2 flex justify-between"><span className="font-semibold">결제할 금액</span><span className="font-bold text-blue-600 text-lg">{totalPrice.toLocaleString()}원</span></div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>

        <div className="px-6 pb-5 pt-3 flex justify-end gap-2">
          <button type="button" className="h-9 px-3.5 rounded-lg text-sm font-semibold bg-gray-100 text-gray-900 hover:bg-gray-200" onClick={requestClose}>취소</button>
          <button type="button" className="h-9 px-3.5 rounded-lg text-sm font-semibold bg-primary-600 text-white hover:bg-blue-700 flex items-center justify-center min-w-[100px]" onClick={handleSubmit} disabled={loading || refreshing}>{loading ? '처리중...' : (refreshing ? '갱신중...' : '예약하기')}</button>
        </div>
      </div>
    </div>
  );
};

export default GeneralReservationModal;
